import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { useTabsContext } from './useTabsContext';
import { TabValueType } from './interfaces';

export interface TabPanelProps {
  value: TabValueType;
  keepMounted?: boolean | undefined;
  forceOpen?: boolean | undefined;
  activeTabClassName?: string;
}

export function TabPanel({
  value,
  keepMounted = false,
  forceOpen = false,
  activeTabClassName,
  children
}: PropsWithChildren<TabPanelProps>) {
  const { selectedTab, onSelected, panelComponentMountMap } = useTabsContext();
  const [isComponentMounted, setComponentMounted] = useState(false);
  const childRef = useRef<HTMLDivElement>(null!);

  const canShowCurrentTab = selectedTab === value;

  useEffect(() => {
    /**
     * This makes sure that when we pass keepMounted=true, we do not render all the
     * components even if they were not visited but only render which were visited.
     * We can d-none to hide components but to keep them in dom
     * */
    if (canShowCurrentTab && !panelComponentMountMap.get(value)) {
      panelComponentMountMap.set(value, true);
      setComponentMounted(true);
    }

    const forceOpenTab =
      forceOpen &&
      !canShowCurrentTab &&
      childRef.current &&
      childRef.current.querySelectorAll('.Mui-error').length;

    if (forceOpenTab) {
      onSelected(null, value);
    }
  }, [forceOpen, canShowCurrentTab]);

  if (keepMounted) {
    return (
      <div
        ref={childRef}
        id={`tabpanel-${value}`}
        role='tabpanel'
        aria-labelledby={`tab-${value}`}
        className={clsx(
          canShowCurrentTab && activeTabClassName,
          'w-100',
          !canShowCurrentTab && 'd-none'
        )}
      >
        {isComponentMounted && children}
      </div>
    );
  }

  return (
    <div
      ref={childRef}
      id={`tabpanel-${value}`}
      role='tabpanel'
      aria-labelledby={`tab-${value}`}
      className={clsx('w-100', canShowCurrentTab && activeTabClassName)}
    >
      {canShowCurrentTab && children}
    </div>
  );
}
